/**
 * TrailerNotificationCard - Notification card for Trailer-type notifications
 *
 * Matches Xamarin NotificationListMobile.xaml trailer notification Frame:
 * - Left color bar: 5px wide, color from TrailerStatusTextColorConverter
 * - Row 0: "Trailer Status Changed To {status}"
 * - Row 1: "Trailer ID: {id} | Trailer Name: {name} | Trailer Status: {icon} {status} | Current Location: {loc}"
 * - Row 2: "X ago" time label
 * - Background: new=#F2F5F8, read=white
 * - Border: #E0E7EE
 */

import { useTranslation } from 'react-i18next';

import { cn } from '@/utils';

import type { NotificationModel } from '@/types';

interface TrailerNotificationCardProps {
  notification: NotificationModel;
  onPress: (notification: NotificationModel) => void;
}

export function TrailerNotificationCard({ notification, onPress }: TrailerNotificationCardProps) {
  const { t } = useTranslation();
  const handlePress = () => {
    onPress(notification);
  };

  return (
    <button
      onClick={handlePress}
      className={cn(
        'flex w-full overflow-hidden rounded border border-notification-border text-left',
        notification.isNew ? 'bg-notification-bg-new' : 'bg-white'
      )}
    >
      {/* Left color bar — Matches Xamarin BoxView WidthRequest=5, Margin="0,0,10,0" */}
      <div className="w-[5px] shrink-0 self-stretch bg-more-option-text" />

      {/* Content area — Matches Xamarin: BoxView right Margin=10 provides gap from bar */}
      <div className="flex flex-1 flex-col pl-[10px] pr-[10px]">
        {/* Row 0: Trailer status header
            Matches Xamarin: Margin="-10,14,0,0" (top=14px), FontSize=12(Phone) */}
        <p className="mt-[14px] font-poppins text-[15px] text-task-details">
          {t('notification.trailerStatusLabel')} {notification.trailerTypeStatusName}
        </p>

        {/* Row 1: Trailer details — Matches Xamarin: StackLayout Margin="-10,10,0,0" (top=10px)
            Each label PoppinsRegular FontSize=11(Phone), TextColor=EntryTextColor */}
        <div className="mt-[14px] flex flex-col gap-[4px]">
          <span className="font-poppins text-[14px] text-entry-text">
            {t('notification.trailerId')}:{' '}
            {notification.trailerId != null ? String(notification.trailerId) : ''}
          </span>
          <span className="font-poppins text-[14px] text-entry-text">
            {t('notification.trailerName')}: {notification.trailerName}
          </span>
          <span className="font-poppins text-[14px] text-entry-text">
            {t('notification.trailerStatus')}: {notification.trailerTypeStatusName}
          </span>
          <span className="font-poppins text-[14px] text-entry-text">
            {t('notification.currentLocation')}: {notification.currentLocation}
          </span>
        </div>

        {/* Row 2: Time ago — Matches Xamarin: Margin="0,0,5,0", FontSize=10(Phone),
            HorizontalOptions=EndAndExpand */}
        <span className="mt-[8px] mb-[10px] mr-[5px] self-end font-poppins text-[14px] text-notification-ago whitespace-nowrap">
          {notification.updatedAgo}
        </span>
      </div>
    </button>
  );
}
